/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { AssetId, AssetSet } from "types/Asset";
import { ClipId, PlaylistClip } from "types/Clip";
import { AudioPlayer, AudioPlayerEventMap } from "../AudioPlayer";
import store from "reducers/Store";
import { setPlayerPlaybackProgress } from "actions/PlayerActions";
import isEqual from "lodash.isequal";
import { createLogger } from "utilities/Logger";
import { maybeApply } from "utilities/FunctionUtilities";
import { baseClipEqual } from "./PlaylistUtilities";
import { AssetUrlCache } from "./AssetUrlCache";
import { AudioFactory, IAudioFactory } from "./AudioFactory";
import { StreamingClip, StreamingClipState } from "./StreamingClip";
import { PlaylistManager } from "./PlaylistManager";
import { Mutex } from "async-mutex";
import { ITimeUpdater, TimeoutTimeUpdater } from "utilities/TimeUpdater";
import { TypedEventTarget } from "typescript-event-target";
import { BufferingState, PlayingState, WaitingState } from "types/Player";
import { IAssetUrlCache } from "types/AssetUrlCache";

export { StreamingClipState } from "./StreamingClip";

const DEFAULT_PRELOAD_WINDOW_MS = 10_000;
const END_OF_PLAYLIST_DELTA_MS = 5;

/**
 * StreamingAudioPlayer
 *
 * A streaming audio player implementation that handles complex audio playlists with multiple
 * overlapping clips, foreground/background audio, and real-time effects processing. The player
 * manages audio streaming, buffering, and seamless transitions between clips.
 *
 * Architecture:
 * The player is built around several key concepts:
 * 1. Playlist Management - Handles clip sequencing and timing via PlaylistManager
 * 2. Audio Streaming - Manages audio element lifecycle via StreamingClip
 * 3. State Management - Coordinates player state, buffering, and transitions
 * 4. Resource Management - Handles asset URLs and caching via AssetUrlCache
 *
 * Key Components:
 * - PlaylistManager: Coordinates clip timing and sequencing
 * - StreamingClip: Manages individual audio element lifecycle
 * - AudioContext: Web Audio API context for audio processing
 * - TimeUpdater: Handles precise playback timing
 * - AssetUrlCache: Manages asset URL resolution and caching
 *
 * State Management:
 * The player maintains several state variables:
 * - playingState: Represents desired playback state
 * - bufferingState: Represents whether we are buffering or not
 * - waitingState: Represents whether we are waiting for some external operation to complete before resuming (e.g. video buffering)
 * - clipAudio: Map of active StreamingClip instances, including ones that are preloading
 *
 * Playback Model:
 * Audio playback follows these principles:
 * 1. One foreground clip drives primary playback timing
 * 2. Background clips can overlap with foreground
 * 3. Clips are preloaded within a configurable window
 * 4. Seamless transitions between clips when possible
 *
 * @example Basic Usage
 * ```typescript
 * const player = new StreamingAudioPlayer();
 *
 * // Setup
 * await player.updateAssets({
 *   'asset1': { id: 'asset1', url: 'https://...' }
 * });
 *
 * // Create playlist
 * await player.updatePlaylist([{
 *   id: 'clip1',
 *   assetId: 'asset1',
 *   startTime: 0,
 *   endTime: 5000,
 *   type: BlockTypes.TRANSCRIPTION,
 * }]);
 *
 * // Playback control
 * await player.play();
 * await player.pause();
 * await player.seek(2500);
 * ```
 *
 * @example Working with Local Audio
 * ```typescript
 * // Load local audio file
 * const blob = await fetch('audio.wav').then(r => r.blob());
 * await player.loadLocalAsset('localAudio1', blob);
 *
 * // Use in playlist
 * await player.updatePlaylist([{
 *   id: 'localClip1',
 *   assetId: 'localAudio1',
 *   startTime: 0,
 *   endTime: 3000
 * }]);
 * ```
 *
 * @example Hot-swapping Effects
 * ```typescript
 * const updatedPlaylist = currentPlaylist.map(clip => ({
 *   ...clip,
 *   enhancementInfo: {
 *     // ... snip
 *     strengthPercent: 80
 *   }
 * }));
 * await player.updatePlaylist(updatedPlaylist); // Will hot-swap if possible
 * ```
 */
export class StreamingAudioPlayer
  extends TypedEventTarget<AudioPlayerEventMap>
  implements AudioPlayer
{
  private readonly audioContext: AudioContext;
  private readonly timeUpdater: ITimeUpdater;
  private readonly audioFactory: IAudioFactory;

  private readonly operationMutex: Mutex = new Mutex();

  private assetUrlCache: IAssetUrlCache;
  private playlistManager: PlaylistManager;

  // stores promises while we wait for pre-signed urls to resolve. will help us buffer if we need to resolve
  // a permanent url during playback. Once resolved the element will move to `this.audioElements`
  private clipAudio: Partial<Record<ClipId, StreamingClip>> = {};

  private onPlaybackCompleteCallback: (() => void) | undefined;

  // Whether we want the player to be 'playing'
  private _playingState: PlayingState = PlayingState.paused;
  // Whether the player is buffering
  private _bufferingState: BufferingState = BufferingState.ready;
  // Whether the player is waiting for some external operation to complete before resuming (e.g. video buffering)
  private _waitingState: WaitingState = WaitingState.ready;

  private readonly logger = createLogger("StreamingAudioPlayer");

  private readonly preloadWindowMs = DEFAULT_PRELOAD_WINDOW_MS;

  constructor(
    audioContext: AudioContext = new AudioContext(),
    timeUpdater: ITimeUpdater = new TimeoutTimeUpdater(),
    assetUrlCache: IAssetUrlCache = new AssetUrlCache(),
    audioFactory: IAudioFactory = new AudioFactory()
  ) {
    super();
    this.audioContext = audioContext;
    void this.audioContext.suspend();
    this.timeUpdater = timeUpdater;
    this.timeUpdater.schedule(this.tick);
    this.assetUrlCache = assetUrlCache;
    this.audioFactory = audioFactory;

    this.playlistManager = new PlaylistManager(
      this.logger.createSubLogger("PlaylistManager")
    );
  }

  setAssetUrlCache(assetUrlCache: IAssetUrlCache): void {
    this.assetUrlCache = assetUrlCache;
  }

  // ******************** INTERFACE METHODS ********************

  setPlaybackCompleteCallback(callback: () => void): void {
    this.onPlaybackCompleteCallback = callback;
  }

  async updateAssets(assets: AssetSet): Promise<void> {
    this.assetUrlCache.setAssets(assets);
  }

  /**
   * Updates the player's playlist. If possible, performs a hot-swap update
   * that maintains audio continuity. Otherwise, pauses playback, updates
   * the playlist, and resumes if previously playing.
   *
   * Playlist updates can be classified into two types:
   * 1. Hot-swappable: Minor changes that don't affect timing
   * 2. Rebuilding: Major changes requiring playback interruption
   *
   * @param playlist - The new playlist to use
   *
   * @example
   * // Update with new effects (hot-swappable)
   * await player.updatePlaylist(playlistWithNewEffects);
   *
   * @example
   * // Major update requiring rebuild
   * await player.updatePlaylist(completelyNewPlaylist);
   */
  async updatePlaylist(playlist: PlaylistClip[]): Promise<void> {
    if (this.canHotSwapPlaylist(playlist)) {
      this.playlistManager.updatePlaylist(playlist);
      Object.entries(this.clipAudio).forEach(([clipId, clipStream]) => {
        if (clipStream) {
          const clip = playlist.find((c) => c.id === clipId);
          if (clip) {
            clipStream.hotswap(clip);
          }
        }
      });
      return;
    }

    this.logger.log(`🔥 updatePlaylist() playlist:`, playlist);
    this.playlistManager.updatePlaylist(playlist);
    await this.seek(this.playlistManager.getCurrentTimeMs());
  }

  async seek(seekTimeInMs: number): Promise<void> {
    const seekTimeInSeconds = (seekTimeInMs / 1000).toFixed(2);
    this.logger.log(`⏰ seek() - Seeking to ${seekTimeInSeconds}....`);

    await this.suspendForOperation(async () => {
      this.stopAllClips();
      this.clipAudio = {};

      this.playlistManager.updateTime(seekTimeInMs);

      this.dispatchTypedEvent(
        "seekTo",
        new CustomEvent("seekTo", {
          detail: seekTimeInMs,
        })
      );

      this.preload();

      store.dispatch(
        setPlayerPlaybackProgress(
          this.playlistManager.getCurrentTimeMs(),
          this.playlistManager.getDurationMs()
        )
      );
    });
  }

  /**
   * Starts audio playback for the current playlist.
   *
   * This method resumes the audio context and begins playing the clips
   * defined in the current playlist. It updates the player state to indicate
   * that playback is active. If there are no clips in the playlist, the method
   * will exit without performing any actions.
   *
   * The playback will be managed by the `tick` method, which handles the timing
   * and transitions between clips. The player will continue to check for the
   * current clips and manage their playback state until the playback is paused
   * or stopped.
   *
   * @returns {Promise<void>} A promise that resolves when the playback has started.
   *
   * @example
   * const player = new StreamingAudioPlayer();
   * await player.updatePlaylist([{ id: 'clip1', assetId: 'asset1', startTime: 0, endTime: 5000 }]);
   * await player.play(); // Starts playback of the current playlist
   */
  async play(): Promise<void> {
    if (this.playlistManager.getDurationMs() === 0) {
      return; // TODO: pause and seek to 0?
    }

    const currentClips = this.playlistManager.getCurrentClips();

    this.logger.log(
      `▶️ play() - play ${currentClips.length} currentClips: `,
      currentClips
    );

    this.playingState = PlayingState.playing;
    await this.resume();
  }

  // We should set this when the audio player needs to wait for some external signal before it resumes
  // e.g. Video is buffering
  setWaiting(waitingState: WaitingState): void {
    this._waitingState = waitingState;

    if (waitingState === WaitingState.waiting) {
      this.logger.debug(
        "Suspending audio player because video player is buffering"
      );
      this.suspend();
    } else if (this.shouldBePlaying()) {
      this.logger.debug(
        "Resuming audio player because video player has finished buffering"
      );
      this.resume();
    }
  }

  async pause(): Promise<void> {
    this.logger.log("⏸️ Pausing.....");
    this.timeUpdater.suspend();
    this.playingState = PlayingState.paused;

    this.pauseAllClips();

    await this.audioContext.suspend();
  }

  async reset(): Promise<void> {
    this.stopAllClips();
    this.assetUrlCache.reset();
    this.clipAudio = {};
    this.playlistManager = new PlaylistManager();
  }

  async getAssetAudioBuffer(_: AssetId): Promise<null> {
    return null;
  }

  async loadLocalAsset(
    assetId: AssetId,
    blob: Blob
  ): Promise<AudioBuffer | null> {
    this.assetUrlCache.addLocalAsset(assetId, blob);
    return null; // result is not used anyways
  }

  // ******************** NON-INTERFACE METHODS ***************

  get playingState(): PlayingState {
    return this._playingState;
  }

  get bufferingState(): BufferingState {
    return this._bufferingState;
  }

  get waitingState(): WaitingState {
    return this._waitingState;
  }

  public clipState(clipId: ClipId): StreamingClipState | undefined {
    return this.clipAudio[clipId]?.state;
  }

  public getForegroundClip(): PlaylistClip | undefined {
    return this.playlistManager.getForegroundClip();
  }

  public getCurrentTimeMs(): number {
    return this.playlistManager.getCurrentTimeMs();
  }

  // ******************** PRIVATE METHODS ********************
  private set playingState(value: PlayingState) {
    this._playingState = value;
    this.dispatchTypedEvent(
      "playingStateChanged",
      new CustomEvent("playingStateChanged", {
        detail: value,
      })
    );
  }

  private set bufferingState(value: BufferingState) {
    const previousBufferingState = this._bufferingState;
    this._bufferingState = value;

    if (previousBufferingState !== value) {
      this.dispatchTypedEvent(
        "bufferingStateChanged",
        new CustomEvent("bufferingStateChanged", {
          detail: value,
        })
      );
    }
  }

  private shouldBePlaying = (): boolean => {
    return (
      this._playingState === PlayingState.playing &&
      this._bufferingState === BufferingState.ready &&
      this._waitingState === WaitingState.ready
    );
  };

  private async suspend(): Promise<void> {
    this.logger.log("⏸️ Suspending.....");
    this.timeUpdater.suspend();

    Object.values(this.clipAudio).forEach((clipStream) =>
      clipStream?.suspend()
    );

    await this.audioContext.suspend();
  }

  private async resume(): Promise<void> {
    this.logger.log("▶️ Resuming.....");
    await this.audioContext.resume();
    this.timeUpdater.resume();
  }

  /**
   * The core update loop that manages playback state. This function:
   * 1. Updates current time based on foreground clip, potentially updating our foreground
   *    clip to the next clip in sequence
   * 2. Stops clips that shouldn't be playing
   * 3. Starts clips that should be playing
   * 4. Manages buffering state
   * 5. Triggers preloading of upcoming clips
   *
   * The assumption that is core to this functionality is that there is a single
   * foreground track at any given time, and that the playback should transition linearly from foreground clip to
   * foreground clip. With that assumption, we can let the state of the foreground track dictate the state of the player
   *
   * Note: This function MUST NOT be async as it needs to maintain precise timing.
   * Any async operations are handled through state changes and subsequent ticks.
   */
  private tick = (): void => {
    if (!this.shouldBePlaying()) {
      return;
    }

    maybeApply(this.getPlaylistTimeMsFromForegroundClip(), (playlistTime) =>
      this.playlistManager.updateTime(playlistTime)
    );

    // If current time is at or beyond playlist duration (with delta), stop and reset the player
    if (
      this.playlistManager.getCurrentTimeMs() >=
      this.playlistManager.getDurationMs() - END_OF_PLAYLIST_DELTA_MS
    ) {
      void this._onPlaybackFinished();
      return;
    }

    this.stopOldClips();

    if (this.clipsNeedBuffering()) {
      return;
    }

    this.startNewClips();

    this.preload();

    store.dispatch(
      setPlayerPlaybackProgress(
        this.playlistManager.getCurrentTimeMs(),
        this.playlistManager.getDurationMs()
      )
    );

    const timeToNext = this.playlistManager.getTimeToNextClipTransition();
    if (timeToNext !== undefined) {
      setTimeout(this.tick, timeToNext);
    }
  };

  private allClipsAreReady(): boolean {
    const currentClips = this.playlistManager.getCurrentClips();
    return currentClips.every((clip) => {
      const clipAudio = this.clipAudio[clip.id];
      return clipAudio?.state === StreamingClipState.ready;
    });
  }

  /**
   * Checks if any of the current clips need buffering. If so, enters
   * buffering state and initializes required clips.
   */
  private clipsNeedBuffering(): boolean {
    if (!this.allClipsAreReady()) {
      this.bufferingState = BufferingState.buffering;
      Object.values(this.clipAudio).forEach((clipStream) =>
        clipStream?.suspend()
      );

      const currentClips = this.playlistManager.getCurrentClips();
      currentClips.forEach((clip) => {
        void this._findOrInitializeElementForClip(clip);
      });

      return true;
    }

    return false;
  }

  private startNewClips(): void {
    const currentClips = this.playlistManager.getCurrentClips();
    currentClips.forEach((clip) => this.playClip(clip));
  }

  /**
   * Stops and cleans up audio clips that should no longer be playing at the current
   * timeline position. This method is critical for resource management and ensuring
   * correct audio playback.
   *
   * Behavior:
   * 1. Identifies clips that should be stopped:
   *    - Clips not present in currentClips list
   *    - Clips that are currently playing (not just preloaded)
   * 2. Stops audio playback for these clips
   * 3. Cleans up associated resources
   * 4. Maintains preloaded clips for optimization
   *
   */
  private stopOldClips(): void {
    const currentClips = this.playlistManager.getCurrentClips();
    const currentClipIds = new Set(currentClips.map((clip) => clip.id));
    Object.entries(this.clipAudio)
      .filter(
        ([clipId, clipStream]) =>
          // Should stop if:
          // 1. Not in current clips
          // 2. Stream exists
          // 3. Currently playing (not preloaded)
          !currentClipIds.has(clipId) &&
          clipStream !== undefined &&
          !clipStream.isPaused
      )
      .forEach(([clipId]) => this.stopClipWithId(clipId));
  }

  private getPlaylistTimeMsFromForegroundClip(): number | undefined {
    const foregroundClip = this.playlistManager.getForegroundClip();
    if (foregroundClip === undefined) {
      return undefined;
    }

    const foregroundElem = this.clipAudio[foregroundClip.id];

    if (
      foregroundClip &&
      foregroundElem &&
      foregroundElem.currentTimeMs >= foregroundClip.offset
    ) {
      const currentClipTime =
        foregroundElem.currentTimeMs - foregroundClip.offset;
      return foregroundClip.startTime + currentClipTime;
    }
  }

  /* Builds the audio graph for the given clip and start playback
   *  Note that the audio context time may progress at a slightly different
   * clock rate than the current time of any playing MediaElements. So it's good to rebuild the fades etc
   * as close as possible to when the clip starts playing to avoid any drift.
   *
   * In a future version we may even want to update the fades during a tick to account for drift
   * */
  private playClip(clip: PlaylistClip): void {
    const clipStream = this.clipAudio[clip.id];
    if (!clipStream) {
      this.logger.error(
        "Tried to play clip that hasn't been initialized yet (playback)"
      );
      return;
    }

    clipStream.play(this.playlistManager.getCurrentTimeMs());
  }

  private pauseAllClips = (): void => {
    Object.values(this.clipAudio).forEach((clipStream) => clipStream?.pause());
  };

  private stopAllClips = (): void => {
    Object.keys(this.clipAudio).forEach(this.stopClipWithId);
  };

  private stopClipWithId = (clipId: string): void => {
    const clipStream = this.clipAudio[clipId];

    clipStream?.stop();
    clipStream?.removeEventListener("error", this.onClipError);
    clipStream?.removeEventListener("waiting", this.onClipWaiting);
    clipStream?.removeEventListener("ready", this.onClipReady);
    clipStream?.removeEventListener("ended", this.onClipEnded);

    delete this.clipAudio[clipId];
  };

  preload(): void {
    this.playlistManager
      .getClipsInRange(
        this.playlistManager.getCurrentTimeMs(),
        this.playlistManager.getCurrentTimeMs() + this.preloadWindowMs
      )
      .forEach((clip) => {
        if (!this.clipAudio[clip.id]) {
          this.logger.debug("Preloading clip", clip);
        }
        this._findOrInitializeElementForClip(clip);
      });
  }

  /**
   * Initializes an audio element for a clip if one doesn't already exist.
   * This method handles:
   * - Asset URL resolution
   * - StreamingClip creation and initialization
   * - Race conditions with asset/blob availability
   *
   * The method is async but returns void as the tick function monitors
   * clip states rather than awaiting promises.
   *
   * @param clip - The clip to initialize
   * @throws Error if the clip's asset is not found
   */
  private async _findOrInitializeElementForClip(
    clip: PlaylistClip
  ): Promise<void> {
    if (!this.assetUrlCache.canLoadClip(clip)) {
      // there is a race condition with clips + assets being added to the state
      // vs the audio blob being handed off to the streaming player
      // if we have an asset without a url and no blob url, we should wait for the blob url to be handed off
      return;
    }

    const playlistTime = this.playlistManager.getCurrentTimeMs();

    const existingElement = this.clipAudio[clip.id];
    if (existingElement !== undefined) {
      return existingElement.ready;
    }

    this.logger.log("element not found loading...");

    const newStreamingClip = new StreamingClip(
      this.audioContext,
      this.audioFactory,
      this.assetUrlCache,
      this.logger.createSubLogger("StreamingClip").createSubLogger(clip.id)
    );
    this.clipAudio[clip.id] = newStreamingClip;

    newStreamingClip.addEventListener("error", this.onClipError);
    newStreamingClip.addEventListener("waiting", this.onClipWaiting);
    newStreamingClip.addEventListener("ready", this.onClipReady);
    newStreamingClip.addEventListener("ended", this.onClipEnded);

    await newStreamingClip.initialize(clip, playlistTime);
  }

  private onClipError = (event: CustomEvent<{ clipId: ClipId }>): void => {
    const { clipId } = event.detail;
    const clip = this.playlistManager.getClip(clipId);
    if (!clip) {
      return;
    }

    this.logger.log(`Clip ${clipId} encountered an error.`);

    const allClipsForAsset = this.playlistManager.getClipsWithAsset(
      clip.assetId
    );

    this.suspendForOperation(async (): Promise<void> => {
      allClipsForAsset.forEach((c) => {
        this.stopClipWithId(c.id);
      });
    });
  };

  private onClipWaiting = (event: CustomEvent<{ clipId: ClipId }>): void => {
    const currentClips = this.playlistManager.getCurrentClips();
    if (currentClips.map((c) => c.id).includes(event.detail.clipId)) {
      this.pauseAllClips();
      this.bufferingState = BufferingState.buffering;
    }
  };

  private onClipEnded = (event: CustomEvent<{ clipId: ClipId }>): void => {
    this.playlistManager.progressToNextForegroundClip(event.detail.clipId);
  };

  private onClipReady = (): void => {
    const allFutureClipsReady = Object.values(this.clipAudio).every(
      (clip) => clip?.state === StreamingClipState.ready
    );

    // If we are buffering, make sure all preloaded clips are ready before saying we are ready
    if (
      allFutureClipsReady &&
      this._bufferingState === BufferingState.buffering
    ) {
      this.bufferingState = BufferingState.ready;
    }
  };

  private async _onPlaybackFinished(): Promise<void> {
    this.logger.log("🏁 Playback finished. Resetting state...");

    await this.pause();
    await this.seek(0);

    this.dispatchTypedEvent(
      "playbackComplete",
      new CustomEvent("playbackComplete")
    );

    if (this.onPlaybackCompleteCallback) {
      this.onPlaybackCompleteCallback();
    }
  }

  /**
   * Determines if a new playlist can safely replace the current one without interrupting
   * playback. Hot-swapping allows for smooth transitions when making minor modifications
   * to clips (like adjusting effects) while maintaining audio continuity.
   *
   * A playlist can be hot-swapped if:
   * 1. The new playlist has the same number of clips as the current one
   * 2. The basic structure (clip order, timing, assets) remains unchanged
   * 3. All currently playing clips support hot-swapping to their new versions
   *
   * Common hot-swappable changes include:
   * - Adjusting effect parameters (e.g., changing enhance strength)
   * - Updating metadata
   * - Modifying clip properties that don't affect timing
   *
   * Changes that prevent hot-swapping:
   * - Adding or removing clips
   * - Changing clip order
   * - Modifying clip timing or assets
   * - Changes to clips that are currently playing and don't support hot-swapping
   *
   * @param newPlaylist - The playlist to be validated for hot-swapping
   * @returns true if the playlist can be hot-swapped, false otherwise
   *
   * @example
   * // Adjusting effect strength (likely can hot-swap)
   * const newPlaylist = currentPlaylist.map(clip => ({
   *   ...clip,
   *   enhanceStrength: 0.8
   * }));
   * await player.updatePlaylist(newPlaylist); // Will hot-swap
   *
   * @example
   * // Adding a clip (cannot hot-swap)
   * const newPlaylist = [
   *   ...currentPlaylist,
   *   newClip
   * ];
   * await player.updatePlaylist(newPlaylist); // Will pause and rebuild
   */
  private canHotSwapPlaylist(newPlaylist: PlaylistClip[]): boolean {
    return this.playlistManager.canHotSwapToNewPlaylist(
      newPlaylist,
      (oldClip, newClip) => {
        if (isEqual(oldClip, newClip)) {
          return true;
        }

        if (!baseClipEqual(oldClip, newClip)) {
          this.logger.log(
            "Can't hotswap because base clip details aren't the same"
          );
          return false;
        }

        const clipStream = this.clipAudio[oldClip.id];
        return clipStream?.canHotswap(newClip) ?? true; // if it doesn't exist, we can hotswap because it's not playing
      }
    );
  }

  /**
   * Temporarily suspends playback while executing an operation, then restores the previous
   * playback state. This ensures that modifications to the player's state or audio
   * elements happen safely without audio artifacts or timing issues.
   *
   * The operation is executed in a paused state regardless of whether playback was
   * already paused. If playback was active before the operation, it will be resumed
   * after the operation completes successfully.
   *
   * @param operation - Async function to execute while playback is paused
   * @returns Promise that resolves after the operation completes and playback state is restored
   *
   * @example
   * // Updating playlist
   * await this.suspendForOperation(async () => {
   *   this.timeline.updatePlaylist(newPlaylist);
   * });
   */
  private async suspendForOperation(
    operation: () => Promise<void>
  ): Promise<void> {
    await this.operationMutex.runExclusive(async () => {
      await this.suspend();

      await operation();

      if (this.shouldBePlaying()) {
        await this.resume();
      }
    });
  }
}
